import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import { useRef } from 'react';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { CustomCellProperties } from 'alurkerja-ui';
import { UnlinkTestIssue } from '@/components/testIssue/UnlinkTestIssue';
import { LinkButton } from '@/components/shared/button/LinkButton';
import DetailTestRunModal from '@/components/testRunDetail/ModalDetail';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { IDetailTestRunIssue } from '@/api/testRunDetailIssue';

type ListLinkedTestCaseProps = {
  testIssueId: string;
  toClose?: () => void;
};

interface CustomActionCellProps {
  data: IDetailTestRunIssue;
  toClose?: () => void;
  tableRef: React.RefObject<ExtendedTableLowCodeRef>;
}

const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  toClose,
  tableRef,
}) => {
  return (
    <>
      <LinkButton>
        <DetailTestRunModal
          tesRunDetailId={data.test_run_detail_id!}
          toClose={toClose}
        />
      </LinkButton>
      <UnlinkTestIssue
        id={data.test_run_detail_id!}
        onSuccess={() => tableRef.current?.refetch()}
      />
    </>
  );
};

export const ListLinkedTestCase: React.FC<ListLinkedTestCaseProps> = ({
  testIssueId,
  toClose,
}) => {
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const spec = useCustomSpec(`/api/crud/test-run-detail-issue`, {
    columns: [
      { key: 'case_code', label: 'Case Code' },
      { key: 'test_run_name', label: 'Test Run Name' },
      { key: 'test_case_name', label: 'Title' },
    ],
  });

  const customCell = ({
    name,
    rowValue,
    defaultCell,
  }: CustomCellProperties) => {
    if (name === 'case_code') {
      return <>{rowValue.test_run_detail.test_case?.case_code}</>;
    } else if (name === 'test_run_name') {
      return <>{rowValue.test_run_detail.test_run?.title}</>;
    } else if (name === 'test_case_name') {
      return <>{rowValue.test_run_detail.test_case?.title}</>;
    }
    return defaultCell;
  };

  if (!spec) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section title="Linked Test Cases">
      <ExtendedTableLowCode
        ref={ref}
        defaultFilter={{
          test_issue_id: testIssueId,
        }}
        defaultSort="title"
        defaultSortOrder="desc"
        spec={spec}
        customButtonCreate={HideCustomCreateButton}
        customActionCell={data => (
          <CustomActionCell
            data={data as IDetailTestRunIssue}
            toClose={toClose}
            tableRef={ref}
          />
        )}
        customCell={customCell}
        showFilter={false}
      />
    </Section>
  );
};
